import { useState, useEffect, useCallback } from 'react';
import api from '@/api';
import {
  Users, Plus, Building2, Phone, Mail, AlertTriangle,
  CheckCircle2, Trash2, ChevronDown, ChevronRight,
  Loader2, X, Link2, Wand2
} from 'lucide-react';

const ROLE_LABELS = {
  general_contractor: 'General Contractor',
  subcontractor: 'Subcontractor',
  architect: 'Architect',
  engineer: 'Engineer',
  commissioning_agent: 'Commissioning Agent',
  owner_rep: 'Owner Rep',
  consultant: 'Consultant',
  other: 'Other',
};

function StakeholderCard({ s, onDelete }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-border bg-card rounded-sm" data-testid={`stakeholder-${s.id}`}>
      <div className="flex items-center gap-4 p-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
        <div className="w-9 h-9 rounded-sm bg-primary/10 border border-primary/30 flex items-center justify-center text-primary text-xs font-bold shrink-0">
          {s.company_name?.charAt(0) || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{s.company_name}</p>
          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
            <span className="font-mono">{ROLE_LABELS[s.role] || s.role}</span>
            {s.contact_name && <span>{s.contact_name}</span>}
            {s.divisions?.length > 0 && (
              <span className="flex items-center gap-1"><Link2 className="w-3 h-3" />{s.divisions.length} divisions</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 shrink-0">
          {s.outstanding_count > 0 ? (
            <div className="text-right">
              <p className="font-mono text-sm text-amber-600 dark:text-amber-400">{s.outstanding_count}</p>
              <p className="text-[10px] text-muted-foreground">outstanding</p>
            </div>
          ) : s.total_required > 0 ? (
            <div className="text-right">
              <p className="font-mono text-sm text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="w-4 h-4 inline" /></p>
              <p className="text-[10px] text-muted-foreground">complete</p>
            </div>
          ) : null}
          <span className={`font-mono text-xs px-2 py-0.5 rounded-sm ${
            s.completion_pct >= 80 ? 'bg-emerald-100 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-900'
            : s.completion_pct > 0 ? 'bg-amber-100 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-900'
            : 'bg-secondary text-muted-foreground border border-border'
          }`}>
            {s.completion_pct}%
          </span>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-border/50 space-y-3">
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            {s.contact_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{s.contact_email}</span>}
            {s.contact_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{s.contact_phone}</span>}
          </div>

          {s.divisions?.length > 0 && (
            <div className="space-y-1">
              <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Assigned Divisions</p>
              {s.divisions.map(d => (
                <div key={d.id} className="flex items-center justify-between text-xs p-2 bg-card border border-border rounded-sm">
                  <span className="text-foreground/80"><span className="font-mono text-muted-foreground mr-2">{d.csi_number}</span>{d.trade_name}</span>
                  <span className={d.outstanding > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}>
                    {d.outstanding > 0 ? `${d.outstanding} outstanding` : 'Complete'}
                  </span>
                </div>
              ))}
            </div>
          )}

          <button onClick={(e) => { e.stopPropagation(); onDelete(s.id); }}
            className="flex items-center gap-1 text-xs text-red-500/50 hover:text-red-400 transition-colors">
            <Trash2 className="w-3 h-3" /> Remove
          </button>
        </div>
      )}
    </div>
  );
}

export default function StakeholderPanel({ projectId, divisions }) {
  const [stakeholders, setStakeholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [autoCreating, setAutoCreating] = useState(false);
  const [form, setForm] = useState({ company_name: '', role: 'subcontractor', contact_name: '', contact_email: '', contact_phone: '', division_ids: [] });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get(`/closeout/projects/${projectId}/stakeholders`);
      setStakeholders(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAutoCreate = async () => {
    setAutoCreating(true);
    try {
      await api.post(`/closeout/projects/${projectId}/stakeholders/auto-create`);
      await fetchData();
    } catch (err) { console.error(err); }
    finally { setAutoCreating(false); }
  };

  const handleAdd = async () => {
    if (!form.company_name) return;
    setSaving(true);
    try {
      await api.post(`/closeout/projects/${projectId}/stakeholders`, form);
      setShowAdd(false);
      setForm({ company_name: '', role: 'subcontractor', contact_name: '', contact_email: '', contact_phone: '', division_ids: [] });
      await fetchData();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/closeout/projects/${projectId}/stakeholders/${id}`);
      await fetchData();
    } catch (err) { console.error(err); }
  };

  const toggleDiv = (divId) => {
    setForm(f => ({
      ...f,
      division_ids: f.division_ids.includes(divId)
        ? f.division_ids.filter(d => d !== divId)
        : [...f.division_ids, divId],
    }));
  };

  const inputCls = "w-full bg-card border border-border rounded-sm px-3 py-2 text-xs text-foreground font-mono focus:outline-none focus:border-primary/40/50 placeholder:text-muted-foreground";

  return (
    <div data-testid="stakeholder-panel">
      {/* Controls */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-muted-foreground">{stakeholders.length} stakeholders</p>
        <div className="flex gap-2">
          <button onClick={handleAutoCreate} disabled={autoCreating}
            data-testid="auto-create-stakeholders-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-heading uppercase tracking-wider text-muted-foreground hover:text-primary border border-border hover:border-primary/30 rounded-sm transition-colors">
            {autoCreating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Auto-Create
          </button>
          <button onClick={() => setShowAdd(!showAdd)}
            data-testid="add-stakeholder-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-heading uppercase tracking-wider bg-blue-600 hover:bg-blue-700 text-white rounded-sm transition-colors">
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>
      </div>

      {/* Add Form */}
      {showAdd && (
        <div className="mb-4 p-4 bg-card border border-border rounded-sm space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] text-muted-foreground block mb-1">Company *</label>
              <input className={inputCls} value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} placeholder="JW Electric" />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground block mb-1">Role</label>
              <select className={inputCls} value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground block mb-1">Contact Name</label>
              <input className={inputCls} value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} placeholder="John Smith" />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground block mb-1">Email</label>
              <input className={inputCls} value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} placeholder="john@jwelectric.com" />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground block mb-1">Phone</label>
              <input className={inputCls} value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))} placeholder="(212) 555-0100" />
            </div>
          </div>
          {divisions.filter(d => !d.is_project_level).length > 0 && (
            <div>
              <label className="text-[10px] text-muted-foreground block mb-1">Link Divisions</label>
              <div className="flex flex-wrap gap-1.5">
                {divisions.filter(d => !d.is_project_level).map(d => (
                  <button key={d.id} onClick={() => toggleDiv(d.id)}
                    className={`px-2 py-1 text-[10px] font-mono rounded-sm transition-colors ${
                      form.division_ids.includes(d.id)
                        ? 'bg-primary/15 text-primary border border-primary/30'
                        : 'bg-secondary text-muted-foreground border border-border hover:text-foreground/80'
                    }`}>
                    {d.csi_number}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground">Cancel</button>
            <button onClick={handleAdd} disabled={saving || !form.company_name} data-testid="save-stakeholder-btn"
              className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs rounded-sm transition-colors">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : stakeholders.length === 0 ? (
        <div className="text-center py-12 bg-card border border-dashed border-border rounded-sm">
          <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No stakeholders yet</p>
          <p className="text-xs text-muted-foreground mt-1">Click "Auto-Create" to generate from division vendors</p>
        </div>
      ) : (
        <div className="space-y-2">
          {stakeholders.map(s => <StakeholderCard key={s.id} s={s} onDelete={handleDelete} />)}
        </div>
      )}
    </div>
  );
}
